import { gql } from '@apollo/client';

import {
  priceBreakdown,
  quotePriceBreakdown,
} from '../../graphql/fragments/quotes/priceBreakdown';
import {
  Event,
  EventRequest,
  Quote,
  QuoteLineItem,
  Supplier,
  SupplierRecommendation,
  SupplierReview,
  User,
} from '../../types';
import { QuotePriceDetails } from '../../helpers/quotes/formatQuoteTotals/types';

export type QuoteFields = Pick<
  Quote,
  | 'uuid'
  | 'covers'
  | 'supportText'
  | 'total'
  | 'state'
  | 'old_commission'
  | 'service_fee'
  | 'commission_fee'
  | 'read_timestamp'
  | 'is_excl_vat_price'
  | 'priceBreakdown'
  | 'introMessage'
  | 'published'
  | 'vatStatus'
>;

export type SupplierFields = Pick<
  Supplier,
  | 'uuid'
  | 'profile_picture'
  | 'images'
  | 'name'
  | 'description'
  | 'average_rating'
  | 'category_tier1'
  | 'slug'
  | 'tagline'
  | 'activity'
  | 'vat_number'
  | 'reviewCount'
  | 'rating'
>;

type SupplierFieldsShort = Pick<
  Supplier,
  'images' | 'name' | 'tagline' | 'average_rating' | 'category_tier1'
>;

type ExternalReview = {
  userFullName: string;
  eventType: string;
  date: string;
};

type ReviewFields = Pick<
  SupplierReview,
  'averageRating' | 'fieldPublicComment' | 'authenticated' | 'source'
>;

export type Attachment = {
  filename: string;
  key: string;
  url: string;
};

export type QuoteLineItemsFields = Pick<
  QuoteLineItem,
  | 'title'
  | 'description'
  | 'quantity'
  | 'price'
  | 'order'
  | 'priceBreakdown'
  | 'image'
  | 'priceType'
>;

export type Review = ReviewFields & {
  reviewsExternalEvent: ExternalReview;
  quotes: {
    event_requests: {
      events: Pick<Event, 'date' | 'type'> & {
        users: Pick<User, 'firstName' | 'lastName'>;
      };
    };
  };
};

export type QuoteForRequest = QuoteFields & {
  quotePriceDetails: QuotePriceDetails;
  quote_line_items: Array<QuoteLineItemsFields>;
  supplier: SupplierFields & {
    reccomendations: Array<SupplierRecommendation>;
    reviews: Array<Review>;
  };
  event_requests: Pick<EventRequest, 'uuid' | 'category_tier2' | 'id'> & {
    events: Pick<Event, 'user_id' | 'type' | 'date' | 'date_flexible' | 'id'>;
    quotes: Array<
      Pick<Quote, 'uuid'> & {
        suppliers: SupplierFieldsShort;
      }
    >;
  };
  attachments: Attachment[];
};

export interface QuotesForRequest {
  quotes: Array<QuoteForRequest>;
}

export const QUOTE_FOR_REQUEST = gql`
  query quotes($quoteId: String!, $eventId: String!, $requestId: String!) {
    quotes(
      where: {
        uuid: { equals: $quoteId }
        event_requests: {
          id: { equals: $requestId }
          events: { id: { equals: $eventId } }
        }
      }
    ) {
      uuid
      state
      covers
      total
      supportText
      state
      old_commission
      read_timestamp
      service_fee
      commission_fee
      is_excl_vat_price
      introMessage
      published
      vatStatus
      priceBreakdown {
        ...QuotePriceBreakdown
      }
      quotePriceDetails {
        supplierTakeHomeAmount
        commissionFeeRate
        commissionFeeAmount
        quoteTotalAmount
        serviceFeeRate
        serviceFeeAmount
        protectPlusRate
        protectPlusAmount
        partnerCommissionRate
        partnerCommissionAmount
        affiliateCommissionRate
        affiliateCommissionAmount
        combinedServiceFeeRate
        combinedServiceFeeAmount
        vatAmount
        customerTotalAmount
        customerTotalPriceExclVat
      }
      quote_line_items {
        title
        description
        quantity
        price
        order
        priceType
        priceBreakdown {
          ...PriceBreakdown
        }
        image {
          url
        }
      }
      supplier: suppliers {
        uuid
        profile_picture
        images
        name
        description
        average_rating
        category_tier1
        slug
        tagline
        activity
        vat_number
        reviewCount
        rating {
          overall
          friendliness
          professionalism
          quality
          valueForMoney
        }
        reviews(where: { published: { equals: true } }) {
          averageRating
          fieldPublicComment
          authenticated
          source
          reviewsExternalEvent {
            userFullName
            eventType
            date
          }
          quotes {
            event_requests {
              events {
                date
                type
                users {
                  firstName
                  lastName
                }
              }
            }
          }
        }
        recommendations(take: 1) {
          highlight
          team_member_name
          recommendation
        }
      }
      event_requests {
        uuid
        id
        category_tier2
        events {
          id
          user_id
          type
          date
          date_flexible
        }
        quotes(
          where: {
            uuid: { not: { equals: $quoteId } }
            state: { equals: pending }
          }
          take: 3
        ) {
          uuid
          suppliers {
            images
            name
            tagline
            average_rating
            category_tier1
          }
        }
      }
      attachments {
        filename
        key
        url
      }
    }
  }
  ${quotePriceBreakdown}
  ${priceBreakdown}
`;
